import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PlanningTools = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const planningTools = [
    {
      id: 'budget-calculator',
      title: 'Budget Calculator',
      description: 'Plan your wedding budget with our comprehensive calculator',
      icon: 'Calculator',
      color: 'bg-green-100 text-green-600',
      features: ['Category-wise breakdown', 'Vendor cost estimates', 'Budget tracking', 'Savings tips'],
      path: '/planning-tools/budget-calculator'
    },
    {
      id: 'vendor-wishlist',
      title: 'Vendor Wishlist',
      description: 'Save and organize your favorite vendors',
      icon: 'Heart',
      color: 'bg-red-100 text-red-600',
      features: ['Favorite vendors', 'Comparison tool', 'Notes & ratings', 'Easy sharing'],
      path: '/planning-tools/wishlist'
    },
    {
      id: 'wedding-checklist',
      title: 'Wedding Checklist',
      description: 'Stay organized with our comprehensive wedding timeline',
      icon: 'CheckSquare',
      color: 'bg-blue-100 text-blue-600',
      features: ['Timeline-based tasks', 'Progress tracking', 'Reminders', 'Custom tasks'],
      path: '/planning-tools/checklist'
    },
    {
      id: 'guest-manager',
      title: 'Guest List Manager',
      description: 'Manage your wedding guest list and RSVPs',
      icon: 'Users',
      color: 'bg-purple-100 text-purple-600',
      features: ['Guest database', 'RSVP tracking', 'Seating charts', 'Meal preferences'],
      path: '/planning-tools/guest-manager'
    },
    {
      id: 'wedding-website',
      title: 'Wedding Website Builder',
      description: 'Create a beautiful wedding website for your guests',
      icon: 'Globe',
      color: 'bg-orange-100 text-orange-600',
      features: ['Beautiful templates', 'RSVP forms', 'Photo gallery', 'Registry links'],
      path: '/planning-tools/website-builder'
    },
    {
      id: 'ai-suggestions',
      title: 'AI Planning Assistant',
      description: 'Get personalized vendor recommendations based on your preferences',
      icon: 'Sparkles',
      color: 'bg-yellow-100 text-yellow-600',
      features: ['Smart recommendations', 'Cultural expertise', 'Budget optimization', 'Timeline suggestions'],
      path: '/planning-tools/ai-assistant'
    }
  ];

  const handleToolClick = (tool) => {
    navigate(tool.path);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wedding Planning Tools | ShaadiNest</title>
        <meta 
          name="description" 
          content="Comprehensive wedding planning tools for South Asian weddings. Budget calculator, vendor wishlist, checklist, and more." 
        />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                Wedding Planning Tools
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                Everything you need to plan your perfect South Asian wedding. From budget calculators to vendor management, we've got you covered.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  iconName="Play"
                  onClick={() => setActiveTab('tools')}
                >
                  Start Planning
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="BookOpen"
                  onClick={() => navigate('/blog')}
                >
                  Planning Guide
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Planning Tools Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {planningTools.map((tool) => (
                <div
                  key={tool.id}
                  className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden group cursor-pointer"
                  onClick={() => handleToolClick(tool)}
                >
                  <div className="p-6">
                    <div className={`w-16 h-16 rounded-xl ${tool.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon name={tool.icon} size={32} />
                    </div>
                    
                    <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                      {tool.title}
                    </h3>
                    
                    <p className="text-text-secondary mb-4">
                      {tool.description}
                    </p>
                    
                    <div className="space-y-2 mb-6">
                      {tool.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Icon name="Check" size={16} className="text-success" />
                          <span className="text-sm text-text-secondary">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      iconName="ArrowRight"
                      iconPosition="right"
                      className="group-hover:bg-primary group-hover:text-white group-hover:border-primary"
                    >
                      Get Started
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Planning Process */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Our Planning Process
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Follow our proven 4-step process to plan your perfect South Asian wedding
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: 1,
                  title: 'Set Your Budget',
                  description: 'Use our budget calculator to determine your wedding budget and allocate funds wisely',
                  icon: 'DollarSign'
                },
                {
                  step: 2,
                  title: 'Create Your Timeline',
                  description: 'Follow our comprehensive checklist to stay organized and on track',
                  icon: 'Calendar'
                },
                {
                  step: 3,
                  title: 'Find Vendors',
                  description: 'Search and shortlist vendors that match your style and budget',
                  icon: 'Search'
                },
                {
                  step: 4,
                  title: 'Manage Details',
                  description: 'Use our tools to manage guest lists, RSVPs, and final details',
                  icon: 'Settings'
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name={step.icon} size={24} className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-white">{step.step}</span>
                    </div>
                  </div>
                  
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {step.title}
                  </h3>
                  
                  <p className="text-text-secondary">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary to-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl text-white mb-4">
              Ready to Start Planning?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of couples who have used our tools to plan their perfect wedding
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calculator"
                onClick={() => navigate('/planning-tools/budget-calculator')}
              >
                Start with Budget Calculator
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="CheckSquare"
                className="border-white text-white hover:bg-white hover:text-primary"
                onClick={() => navigate('/planning-tools/checklist')}
              >
                View Wedding Checklist
              </Button>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default PlanningTools;